
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ArrowRight, Calendar, XCircle, Shield, MessageCircle, RefreshCw, ChevronDown, AlertCircle, Lightbulb, Eye } from 'lucide-react';
import { Language, View } from '../types';
import { goldenSpring, swissStaggerContainer, swissCardVariants } from '../lib/animations';

interface HowIHelpPageProps {
  lang: Language;
  setView: (v: View) => void;
}

const HowIHelpPage: React.FC<HowIHelpPageProps> = ({ lang, setView }) => {

  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const content = {
    label: { en: "PROCESS", fa: "فرایند" },
    heroTitle: {
      en: <>Redesigning<br/><span className="swiss-accent text-primary">The Inner System</span></>,
      fa: <>بازطراحی<br/><span className="swiss-accent text-primary">سیستم درونی</span></>
    },
    introText: {
      en: "There are areas of our lives we wish were different. A healthier body, more meaningful work, deeper relationships, financial freedom, courage to express ourselves, peace of mind...",
      fa: "جاهایی از زندگی‌مون هست که دوست داریم متفاوت باشه. بدنِ سالم‌تر، کارِ معنادارتر، رابطه‌ی عمیق‌تر، آزادی مالی، جرأت ابراز خودمون، آرامش ذهنی..."
    },
    problemText: {
      en: "We start many times, get motivated, make some progress... and then let go again. We conclude \"I have no discipline,\" \"I'm not smart,\" \"I'm weak-willed,\" \"I'm unmotivated,\" or \"I have no guts.\" Then we blame ourselves, getting angry and hopeless.",
      fa: "بارها شروع می‌کنیم، انگیزه می‌گیریم، یه‌کم پیشرفت می‌کنیم... و دوباره رها می‌کنیم. نتیجه می‌گیریم که «انضباط ندارم»، «باهوش نیستم»، «اراده‌ام ضعیفه»، «انگیزه ندارم» یا «جرأت ندارم». بعد خودمون رو سرزنش می‌کنیم، عصبانی و ناامید می‌شیم."
    },
    solutionText: {
      en: "But the problem is usually not a \"character weakness.\" This is where coaching comes in.",
      fa: "اما مشکل معمولاً «ضعف شخصیتی» نیست. اینجاست که کوچینگ وارد می‌شه."
    },
    coachingHelps: {
      en: "Coaching helps us see the hidden reasons behind these inner patterns and understand how to change them.",
      fa: "کوچینگ کمک می‌کنه دلایل پنهان پشت این الگوهای درونی رو ببینیم و بفهمیم چطور می‌شه تغییرشون داد."
    },
    quoteText: {
      en: "\"Coaching is a structured partnership based on becoming aware of our inner system and redesigning it to build the life we deeply want.\"",
      fa: "«کوچینگ یک همراهی ساختاریافته‌ست برای آگاه شدن از سیستم درونی‌مون و بازطراحی اون برای ساختن زندگی‌ای که از عمق وجودمون می‌خوایم.»"
    },
    exampleTitle: { en: "A Simple Example", fa: "یک مثال ساده" },
    exampleIntro: {
      en: "Imagine you want to publish your project, but you scroll through social media for hours.",
      fa: "تصور کن می‌خوای پروژه‌ات رو منتشر کنی، ولی ساعت‌ها توی شبکه‌های اجتماعی اسکرول می‌کنی."
    },
    surfaceLevel: { en: "Surface Level", fa: "سطح ظاهری" },
    surfaceDesc: { en: "\"I'm lazy or procrastinating.\"", fa: "«تنبلم یا اهمال‌کاری می‌کنم.»" },
    deeperLevel: { en: "Deeper Level", fa: "سطح عمیق‌تر" },
    deeperDesc: { en: "A part of you is protecting you from shame, judgment, failure, or pressure.", fa: "بخشی از تو داره ازت در برابر شرم، قضاوت، شکست یا فشار محافظت می‌کنه." },
    realization: {
      en: "Here you realize your brain has subconscious goals that conflict with your conscious goals.",
      fa: "اینجا متوجه می‌شی که مغزت اهداف ناخودآگاهی داره که با اهداف آگاهانه‌ات در تضاده."
    },
    gameChanger: {
      en: "And here the game changes completely. The solution can be something else entirely: Instead of force, artificial discipline, and self-blame (which you've tried many times), you understand you need to protect yourself from the damage of blame. Or change your view on others' feedback. Or check if you even need to defend yourself anymore or if your brain is responding to a past memory! Maybe you just need more emotional support.",
      fa: "و اینجا بازی کاملاً عوض می‌شه. راه‌حل می‌تونه چیز دیگه‌ای باشه: به جای زور، انضباط مصنوعی و خودسرزنشی (که بارها امتحان کردی)، می‌فهمی که باید خودت رو از آسیب سرزنش محافظت کنی. یا دیدت رو نسبت به بازخورد دیگران عوض کنی. یا بررسی کنی که آیا اصلاً هنوز نیاز به دفاع از خودت داری یا مغزت داره به یک خاطره‌ی قدیمی واکنش نشون می‌ده! شاید فقط به حمایت عاطفی بیشتر نیاز داری."
    },
    coachingConclusion: {
      en: "Coaching is where these layers are seen, and new solutions are discovered. Where inner wars turn into empathy and conscious choice.",
      fa: "کوچینگ جاییه که این لایه‌ها دیده می‌شن و راه‌حل‌های جدید کشف می‌شن. جایی که جنگ‌های درونی به همدلی و انتخاب آگاهانه تبدیل می‌شن."
    },
    resultsTitle: { en: "What Results Do We Experience?", fa: "چه نتایجی تجربه می‌کنیم؟" },
    notTitle: { en: "What Coaching Is Not", fa: "کوچینگ چی نیست" },
    notForTitle: { en: "Who is this Coaching NOT For?", fa: "این کوچینگ برای چه کسایی نیست؟" },
    approachTitle: { en: "My Specific Approach", fa: "رویکرد من" },
    approachIntro: {
      en: "My work is grounded in modern psychology frameworks: Internal Family Systems (IFS) and Nonviolent Communication (NVC). They help us recognize our inner system and build deeper connections with ourselves and others.",
      fa: "کار من بر پایه‌ی چارچوب‌های روانشناسی مدرن بنا شده: سیستم‌های خانواده‌ی درونی (IFS) و ارتباط بدون خشونت (NVC). اینا کمک می‌کنن سیستم درونی‌مون رو بشناسیم و با خودمون و دیگران ارتباط عمیق‌تری بسازیم."
    },
    approachKey: {
      en: "Our inner system is working perfectly for the goals it currently has. If we want different results, we need to optimize the system for a different goal. We often think the system is broken and try to change it with pressure.",
      fa: "سیستم درونی ما کاملاً درست برای اهدافی که الان داره کار می‌کنه. اگه نتایج متفاوتی می‌خوایم، باید سیستم رو برای هدف متفاوتی بهینه کنیم. اغلب فکر می‌کنیم سیستم خرابه و سعی می‌کنیم با فشار تغییرش بدیم."
    },
    faqTitle: { en: "Common Questions", fa: "سوالات رایج" },
    readyTitle: { en: "Ready?", fa: "آماده‌ای؟" },
    readyDesc: {
      en: "An honest chat. To answer your questions and explore your path.\nFree, 30-60 mins, no obligation.",
      fa: "یک گفتگوی صادقانه. برای پاسخ به سوالاتت و کشف مسیرت.\nرایگان، ۳۰ تا ۶۰ دقیقه، بدون تعهد."
    },
    discoveryCta: { en: "Request Discovery Session", fa: "درخواست جلسه‌ی آشنایی" },
    learnMoreCta: { en: "Explore my Methodology", fa: "کاوش در روش‌شناسی من" }
  };

  const results = [
    {
      title: { en: "Radical Clarity", fa: "وضوح رادیکال" },
      desc: {
        en: "Understanding what you truly want, not what you think you 'should' want. Maybe you think you want freedom, but deeply prioritize safety. As long as these aren't aligned, we want one thing but do another. Or maybe you need to be seen and validated. Sometimes real change starts with changing the goal, not more force.",
        fa: "فهمیدن اینکه واقعاً چی می‌خوای، نه چیزی که فکر می‌کنی «باید» بخوای. شاید فکر می‌کنی آزادی می‌خوای، ولی در عمق امنیت رو اولویت می‌دی. تا وقتی اینا هم‌راستا نباشن، یه چیز می‌خوایم ولی یه کار دیگه می‌کنیم. یا شاید نیاز داری دیده و تأیید بشی. گاهی تغییر واقعی از عوض کردن هدف شروع می‌شه، نه زور بیشتر."
      }
    },
    {
      title: { en: "Self-Awareness", fa: "خودآگاهی" },
      desc: {
        en: "Observing your inner system and behavioral patterns. Why perfectionism? Why delaying decisions? Every pattern or system in us was built for real, important reasons. Changing them without understanding and respecting this reality is usually unsustainable and leads to more self-blame and pressure.",
        fa: "مشاهده‌ی سیستم درونی و الگوهای رفتاری‌ات. چرا کمال‌گرایی؟ چرا تعویق تصمیم‌ها؟ هر الگو یا سیستمی در ما به دلایل واقعی و مهمی ساخته شده. تغییر دادنشون بدون فهمیدن و احترام به این واقعیت معمولاً پایدار نیست و به خودسرزنشی و فشار بیشتر می‌انجامه."
      }
    },
    {
      title: { en: "Action Without Force", fa: "اقدام بدون زور" },
      desc: {
        en: "Moving from 'I have to' to 'I want to'. Life doesn't change with insight alone. But action without insight is often exhausting. Coaching holds both together.",
        fa: "حرکت از «باید» به «می‌خوام». زندگی فقط با بینش تغییر نمی‌کنه. ولی اقدام بدون بینش هم معمولاً فرسایشیه. کوچینگ هر دو رو با هم نگه می‌داره."
      }
    },
    {
      title: { en: "Companionship & Accountability", fa: "همراهی و تعهد" },
      desc: {
        en: "A coach as a companion on the journey helps you stay committed, not stop, and is by your side during hard choices so you actually live what you know.",
        fa: "کوچ به عنوان همراه در مسیر کمکت می‌کنه متعهد بمونی، متوقف نشی، و در لحظه‌های انتخاب سخت کنارت باشه تا واقعاً اون چیزی که می‌دونی رو زندگی کنی."
      }
    }
  ];

  const notItems = [
    {
      title: { en: "Not Therapy", fa: "تراپی نیست" },
      desc: {
        en: "Therapy works more with past wounds; coaching focuses on creating the future. For active trauma/PTSD, therapy is the starting point.",
        fa: "تراپی بیشتر با زخم‌های گذشته کار می‌کنه؛ کوچینگ روی ساختن آینده تمرکز داره. برای تروما یا PTSD فعال، تراپی نقطه‌ی شروعه."
      }
    },
    {
      title: { en: "Not Consulting", fa: "مشاوره نیست" },
      desc: {
        en: "I don't say what's right or wrong. The premise is you are the expert of your life. Coaching helps you access that wisdom.",
        fa: "من نمی‌گم چی درسته چی غلط. فرض اینه که تو متخصص زندگی خودتی. کوچینگ کمک می‌کنه به اون خرد دسترسی پیدا کنی."
      }
    },
    {
      title: { en: "Not Magic", fa: "جادو نیست" },
      desc: {
        en: "Transformation doesn't happen without your participation. It's joint work requiring your real action.",
        fa: "تحول بدون مشارکت تو اتفاق نمی‌افته. این یک کار مشترکه که نیاز به اقدام واقعی تو داره."
      }
    }
  ];

  const notForItems = [
    { en: "Those looking for ready-made prescriptions and quick answers", fa: "کسایی که دنبال نسخه‌های آماده و جواب‌های سریع هستن" },
    { en: "Those unwilling to face their inner self", fa: "کسایی که حاضر نیستن با درون خودشون روبرو بشن" },
    { en: "Those not taking responsibility for change", fa: "کسایی که مسئولیت تغییر رو نمی‌پذیرن" },
    { en: "Those not committed to attending sessions", fa: "کسایی که به حضور در جلسات متعهد نیستن" }
  ];

  const faqs = [
    {
      q: { en: "What if I don't know what I want?", fa: "اگه ندونم چی می‌خوام چی؟" },
      a: {
        en: "That's often where we start. Clarity emerges during the work, not before it.",
        fa: "اغلب از همین‌جا شروع می‌کنیم. شفافیت قبل از کار نمیاد، بلکه در حین کار پیدا می‌شه."
      }
    },
    {
      q: { en: "How long does coaching take?", fa: "کوچینگ چقدر طول می‌کشد؟" },
      a: {
        en: "No set time. We determine a time based on the result you want and move towards it. Some see changes in months, others work for a year. ICF suggests a minimum of 3 months.",
        fa: "زمان مشخصی نداره. با توجه به نتیجه‌ای که می‌خوای بهش برسی با هم زمانی تعیین می‌کنیم و در جهتش پیش می‌ریم. بعضی‌ها در چند ماه تغییر می‌بینند، بعضی یک سال کار می‌کنند. فدراسیون کوچینگ حداقل زمان ۳ ماه رو پیشنهاد می‌ده."
      }
    },
    {
      q: { en: "Is it going to be hard and uncomfortable?", fa: "آیا قراره سخت و ناراحت‌کننده باشه؟" },
      a: {
        en: "Hard, yes, but not necessarily upsetting. Growth requires facing fears and leaving the comfort zone. This difficulty is rewarding.",
        fa: "سخت بله اما ناراحت کننده فکر نکنم. رشد نیازمند روبرو شدن با ترس‌هاست. نیازمند خارج شدن از منطقه‌ی امنه که می‌تونه سخت باشه. اما این سختی خوش‌آینده."
      }
    },
    {
      q: { en: "Difference from self-help books?", fa: "چه فرقی با کتاب‌های خودیاری داره؟" },
      a: {
        en: "Books give info; coaching is discovery. What you discover yourself is easier to apply. Plus, you have support and accountability in a 'two-person relationship'.",
        fa: "کتاب اطلاعات می‌ده اما با کوچینگ خودت کشف می‌کنی. برای همینه که تاثیرگذارتره چون آدم چیزی که خودش کشف می‌کنه رو بهتر و راحت‌تر در زندگیش اعمال می‌کنه تا چیزی که یه نفر دیگه بهش می‌گه. بعلاوه در کوچینگ حمایت، پاسخگویی و دیدن نقاط کور رو از سمت من داری. که این کار در «رابطه‌ی دو انسان» اتفاق می‌افته."
      }
    }
  ];

  const pillars = [
    { icon: RefreshCw, title: { en: "Systems Thinking", fa: "تفکر سیستمی" }, desc: { en: "Insight for growth without judgment.", fa: "بینش برای رشد بدون قضاوت." } },
    { icon: Shield, title: { en: "IFS", fa: "IFS" }, desc: { en: "Turning inner conflict into self-compassion.", fa: "تبدیل تضاد درونی به شفقت با خود." } },
    { icon: MessageCircle, title: { en: "NVC", fa: "NVC" }, desc: { en: "Connecting with clarity and empathy.", fa: "ارتباط با شفافیت و همدلی." } }
  ];

  return (
    <div className="min-h-dvh pt-28 pb-20 px-6 lg:px-12 bg-white">
      <div className="max-w-4xl mx-auto">

        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={goldenSpring}
          className="mb-16"
        >
          <h1 className="swiss-headline text-4xl md:text-5xl lg:text-6xl text-brandDark mb-6">
            {content.heroTitle[lang]}
          </h1>
        </motion.div>

        {/* Introduction */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ ...goldenSpring, delay: 0.1 }}
          className="mb-16"
        >
          <div className="space-y-6">
            <p className="swiss-body text-lg text-gray-600 leading-relaxed">{content.introText[lang]}</p>
            <p className="swiss-body text-lg text-gray-600 leading-relaxed">{content.problemText[lang]}</p>
            <p className="swiss-subhead text-xl text-brandDark">{content.solutionText[lang]}</p>
            <p className="swiss-body text-lg text-gray-600 leading-relaxed">{content.coachingHelps[lang]}</p>

            {/* Quote block */}
            <div className={`p-8 bg-stone-50 ${lang === 'fa' ? 'border-r-4' : 'border-l-4'} border-primary rounded-lg mt-8`}>
              <p className="swiss-accent text-brandDark text-lg md:text-xl leading-relaxed">
                {content.quoteText[lang]}
              </p>
            </div>

            <div className="pt-4">
              <a
                href="https://calendar.app.google/NbuyxdkKnzVU9gj17"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary btn-md"
              >
                <Calendar className="w-5 h-5" />
                {content.discoveryCta[lang]}
              </a>
            </div>
          </div>
        </motion.section>

        <div className="w-full h-px bg-gray-100 mb-16" />

        {/* Example Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={goldenSpring}
          className="mb-16"
        >
          <h2 className="swiss-headline text-3xl md:text-4xl text-brandDark mb-8">
            {content.exampleTitle[lang]}
          </h2>

          <p className="swiss-body text-lg text-gray-600 mb-8">{content.exampleIntro[lang]}</p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Surface Level */}
            <div className="p-6 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <Eye className="w-5 h-5 text-gray-400" />
                <span className="swiss-label text-gray-500 text-xs">{content.surfaceLevel[lang]}</span>
              </div>
              <p className="swiss-body text-brandDark text-lg">{content.surfaceDesc[lang]}</p>
            </div>

            {/* Deeper Level */}
            <div className="p-6 bg-primary/5 rounded-xl border border-primary/20">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className="w-5 h-5 text-primary" />
                <span className="swiss-label text-primary text-xs">{content.deeperLevel[lang]}</span>
              </div>
              <p className="swiss-body text-brandDark text-lg">{content.deeperDesc[lang]}</p>
            </div>
          </div>

          <div className="space-y-4">
            <p className="swiss-subhead text-lg text-brandDark">{content.realization[lang]}</p>
            <p className="swiss-body text-gray-600 leading-relaxed">{content.gameChanger[lang]}</p>
            <p className="swiss-body text-primary font-medium text-lg">{content.coachingConclusion[lang]}</p>
          </div>
        </motion.section>

        <div className="w-full h-px bg-gray-100 mb-16" />

        {/* Results Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={goldenSpring}
          className="mb-16"
        >
          <h2 className="swiss-headline text-3xl md:text-4xl text-brandDark mb-10">
            {content.resultsTitle[lang]}
          </h2>

          <motion.div
            variants={swissStaggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-4"
          >
            {results.map((item, i) => (
              <motion.div
                key={i}
                variants={swissCardVariants}
                className="flex gap-5 items-start p-6 border border-gray-100 rounded-xl hover:shadow-lg hover:border-gray-200 transition-all"
              >
                <div className="mt-0.5 bg-primary/10 text-primary rounded-full p-2 shrink-0">
                  <Check className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="swiss-subhead text-brandDark text-lg mb-2">{item.title[lang]}</h3>
                  <p className="swiss-body text-gray-500 text-base leading-relaxed">{item.desc[lang]}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.section>

        {/* What Coaching Is Not */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={goldenSpring}
          className="mb-16"
        >
          <h2 className="swiss-headline text-3xl md:text-4xl text-brandDark mb-10">
            {content.notTitle[lang]}
          </h2>
          <motion.div
            variants={swissStaggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-6"
          >
            {notItems.map((item, i) => (
              <motion.div
                key={i}
                variants={swissCardVariants}
                className="p-6 bg-stone-50 rounded-xl"
              >
                <div className="mb-4 text-gray-400"><XCircle className="w-6 h-6" /></div>
                <h4 className="swiss-subhead text-brandDark mb-2">{item.title[lang]}</h4>
                <p className="swiss-body text-gray-500 text-sm leading-relaxed">{item.desc[lang]}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.section>

        {/* My Approach Summary */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={goldenSpring}
          className="mb-16"
        >
          <div className="bg-brandDark text-white p-8 md:p-12 rounded-2xl relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-primary/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-teal-400/10 rounded-full blur-2xl" />

            <div className="relative z-10">
              <h2 className="swiss-headline text-3xl md:text-4xl text-white mb-6">
                {content.approachTitle[lang]}
              </h2>
              <p className="swiss-body text-lg text-white/80 leading-relaxed mb-4 text-pretty">
                {content.approachIntro[lang]}
              </p>
              <p className="swiss-body text-lg text-white/80 leading-relaxed mb-8 text-pretty">
                {content.approachKey[lang]}
              </p>

              {/* Three Pillars Summary */}
              <div className="grid md:grid-cols-3 gap-4 mb-8">
                {pillars.map((pillar, i) => (
                  <div key={i} className="p-4 border border-white/10 rounded-xl bg-white/5">
                    <div className="flex items-center gap-2 mb-2">
                      <pillar.icon className="w-5 h-5 text-primary" />
                      <span className="swiss-subhead text-white">{pillar.title[lang]}</span>
                    </div>
                    <p className="swiss-body text-sm text-white/50">{pillar.desc[lang]}</p>
                  </div>
                ))}
              </div>

              <button
                onClick={() => setView('approach')}
                className="group inline-flex items-center gap-2 text-brandDark bg-white font-semibold px-6 py-3 rounded-full hover:bg-gray-100 transition-colors"
              >
                {content.learnMoreCta[lang]}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </motion.section>

        {/* Who is this NOT for */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={goldenSpring}
          className="mb-16"
        >
          <h2 className="swiss-headline text-3xl md:text-4xl text-brandDark mb-8">
            {content.notForTitle[lang]}
          </h2>
          <ul className="space-y-3">
            {notForItems.map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                <span className="swiss-body text-gray-600">{item[lang]}</span>
              </li>
            ))}
          </ul>
        </motion.section>

        {/* FAQ Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={goldenSpring}
          className="mb-16"
        >
          <h2 className="swiss-headline text-3xl md:text-4xl text-brandDark mb-10">
            {content.faqTitle[lang]}
          </h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-gray-100 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="swiss-subhead text-brandDark">{faq.q[lang]}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <p className="swiss-body text-gray-600 px-5 pb-5">{faq.a[lang]}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={goldenSpring}
        >
          <div className="bg-stone-50 p-10 md:p-14 rounded-2xl text-center">
            <h2 className="swiss-headline text-4xl md:text-5xl text-brandDark mb-4">
              {content.readyTitle[lang]}
            </h2>
            <p className="swiss-body text-gray-500 max-w-md mx-auto text-lg leading-relaxed mb-8 whitespace-pre-line text-pretty">
              {content.readyDesc[lang]}
            </p>
            <a
              href="https://calendar.app.google/NbuyxdkKnzVU9gj17"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary btn-lg"
            >
              <Calendar className="w-5 h-5" />
              {content.discoveryCta[lang]}
            </a>
          </div>
        </motion.section>

      </div>
    </div>
  );
};

export default HowIHelpPage;
